/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";

type ActiveSectionContextValue = {
  section: string;
  setSection: (section: string) => void;
  seasonId?: string;
  setSeason: Dispatch<SetStateAction<string | undefined>>;

  resetToDefault: () => void;
};

type ActiveSectionProviderProps = {
  pickedSeason?: string;
  defaultSection: string;
  children: ReactNode;
};

const ActiveSectionContext = createContext<ActiveSectionContextValue>(
  {} as ActiveSectionContextValue,
);

export const ActiveSectionProvider = (props: ActiveSectionProviderProps) => {
  const { defaultSection, children, pickedSeason } = props;

  const [section, setSection] = useState(defaultSection);
  const [seasonId, setSeason] = useState(pickedSeason);

  const resetToDefault = useCallback(
    () => setSection(defaultSection),
    [defaultSection],
  );

  return (
    <ActiveSectionContext.Provider
      value={{ section, setSection, resetToDefault, seasonId, setSeason }}
    >
      {children}
    </ActiveSectionContext.Provider>
  );
};

export const useActiveSection = (): ActiveSectionContextValue => {
  const context = useContext(ActiveSectionContext);

  if (!context) {
    throw new Error("Component beyond ActiveSectionContext");
  }

  return context;
};
